import * as Sentry from "@sentry/react";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ZodError } from "zod";
import { isNonRetryableClientError } from "../utils/isNonRetryableClientError";

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /**
       * Failures this mutation treats as a normal outcome rather than a bug — a 409 from an
       * endpoint whose conflict the UI explains, say. Matching errors are recorded as a Sentry
       * breadcrumb instead of an issue; everything else the mutation throws is still reported.
       *
       * Narrow the predicate to the errors you actually handle. Handling an error in `onError`
       * is not by itself a reason to list it here: most `onError` handlers exist to show a toast
       * when something genuinely broke, and those failures are the ones worth reporting.
       */
      expectedError?: (error: unknown) => boolean;
    };
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      // oxlint-disable-next-line no-console
      console.error(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      const { mutationKey, meta } = mutation.options;
      if (meta?.expectedError?.(error)) {
        // Keep it in the trail of whatever real error may follow, without opening an issue.
        Sentry.addBreadcrumb({
          category: "mutation",
          level: "info",
          message: `Expected mutation error: ${String(error)}`,
          data: mutationKey === undefined ? undefined : { mutationKey: JSON.stringify(mutationKey) },
        });
        return;
      }
      Sentry.captureException(error, {
        tags: mutationKey === undefined ? undefined : { mutationKey: JSON.stringify(mutationKey) },
      });
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry(failureCount, error) {
        if (error instanceof ZodError) {
          return false;
        }
        if (isNonRetryableClientError(error)) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

export const ReactQueryProvider = ({ children }: { children: React.ReactNode }) => {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};
